import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  NgZone,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Rutas en las que no se ofrece recargar (p. ej. la vuelta del pago): el aviso
 * espera a que el usuario salga de ellas. Recibe la URL del router, sin el
 * prefijo de idioma.
 */
export const APP_UPDATE_EXCLUDED = new InjectionToken<(url: string) => boolean>(
  'APP_UPDATE_EXCLUDED',
  { factory: () => () => false }
);

/** Cada cuánto se pregunta al servidor si hay versión nueva. */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Margen mínimo entre comprobaciones lanzadas por el uso (cambio de pantalla,
 * volver a la pestaña o a la ventana), para no repetirlas al navegar seguido.
 */
const MIN_GAP_MS = 10 * 1000;

/**
 * Aviso de versión nueva tras un deploy (TS569).
 *
 * El deploy deja un `version.json` (`{ "version": "<commit>" }`) en la raíz de lo
 * que se sirve. Este servicio lo consulta al arrancar, cada minuto, en cada
 * cambio de pantalla y al volver a la pestaña o a la ventana; si cambia, muestra
 * un aviso con un botón para recargar.
 * Nunca recarga por su cuenta: es el usuario quien decide cuándo, así que no
 * pierde lo que tenga a medias.
 *
 * Solo en navegador y fuera de la zona de Angular: un temporizador en el render
 * del servidor impediría que la app se estabilizara.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly isExcluded = inject(APP_UPDATE_EXCLUDED);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private started = false;
  private checking = false;
  private lastCheckAt = 0;
  private loadedVersion: string | null = null;

  private readonly updateReady = signal(false);
  private readonly onExcludedRoute = signal(false);

  /** Mostrar el aviso de versión nueva. */
  readonly showNotice = computed(
    () => this.updateReady() && !this.onExcludedRoute()
  );

  start(): void {
    if (!this.isBrowser || this.started) return;
    this.started = true;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.onExcludedRoute.set(this.isExcluded(e.urlAfterRedirects));
        this.zone.runOutsideAngular(() => this.checkSoon());
      });

    this.zone.runOutsideAngular(() => {
      void this.check();
      setInterval(() => void this.check(), CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.checkSoon();
      });
      window.addEventListener('focus', () => this.checkSoon());
    });
  }

  /** Comprobación disparada por el uso, salvo si se acaba de hacer una. */
  private checkSoon(): void {
    if (Date.now() - this.lastCheckAt >= MIN_GAP_MS) void this.check();
  }

  reload(): void {
    location.reload();
  }

  /**
   * Pide `version.json` a la raíz del dominio (ruta absoluta: con una relativa se
   * resolvería bajo `/es/`, `/en/`…), con `fetch` nativo para que el interceptor
   * no le pegue la base de la API, y sin caché.
   */
  private async check(): Promise<void> {
    if (this.updateReady() || this.checking) return;
    this.checking = true;
    this.lastCheckAt = Date.now();
    try {
      const res = await fetch(`${location.origin}/version.json`, {
        cache: 'no-store',
      });
      if (!res.ok) return; // en local no existe: no hace nada
      const { version } = await res.json();
      if (!version) return;

      if (this.loadedVersion === null) {
        this.loadedVersion = version;
      } else if (version !== this.loadedVersion) {
        // De vuelta en la zona para que la vista pinte el aviso
        this.zone.run(() => this.updateReady.set(true));
      }
    } catch {
      // Sin conexión o respuesta rara: se reintenta en la siguiente comprobación
    } finally {
      this.checking = false;
    }
  }
}
